import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = { navy:'#0D1B2A', teal:'#00B4D8', success:'#10B981', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA', amber:'#F59E0B' };

const SECTION_LABELS = {
  listening:'Listening', reading:'Reading', writing:'Writing', speaking:'Speaking',
  grammar:'Grammar', vocabulary:'Vocabulary',
  math_no_calc:'Math (No Calculator)', math_calc:'Math (Calculator)', reading_writing:'Reading & Writing',
  mathematics:'Mathematics', critical_thinking:'Critical Thinking', english:'English',
};

function Timer({ totalSeconds, onExpire }) {
  const [left, setLeft] = useState(totalSeconds);
  useEffect(() => {
    if (left <= 0) { onExpire(); return; }
    const t = setInterval(() => setLeft(l => { if (l <= 1) { onExpire(); return 0; } return l - 1; }), 1000);
    return () => clearInterval(t);
  }, []);
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  const pad = n => String(n).padStart(2, '0');
  const urgent = left < 300;
  return (
    <div style={{ fontFamily:'monospace', fontSize: 22, fontWeight: 800, color: urgent ? C.error : C.navy, background: urgent ? '#FEE2E2' : C.bg, padding:'8px 20px', borderRadius: 10, border:`2px solid ${urgent ? C.error : C.border}` }}>
      {h > 0 && `${pad(h)}:`}{pad(m)}:{pad(s)}
    </div>
  );
}

// Per-section countdown — computes remaining from accumulated section time (survives
// reloads) so each section gets its own clock, like a real IELTS test centre.
function SectionTimer({ sectionKey, durationSec, committedRef, startRef, onExpire }) {
  const calc = () => Math.max(0, Math.round(durationSec - ((committedRef.current[sectionKey] || 0) + (Date.now() - (startRef.current || Date.now())) / 1000)));
  const [left, setLeft] = useState(calc);
  const firedRef = useRef(false);
  useEffect(() => {
    firedRef.current = false;
    setLeft(calc());
    const t = setInterval(() => {
      const r = calc();
      setLeft(r);
      if (r <= 0 && !firedRef.current) { firedRef.current = true; clearInterval(t); onExpire(); }
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey, durationSec]);
  const m = Math.floor(left / 60), s = left % 60;
  const pad = n => String(n).padStart(2, '0');
  const urgent = left < 120;
  return (
    <div style={{ fontFamily:'monospace', fontSize: 22, fontWeight: 800, color: urgent ? C.error : C.navy, background: urgent ? '#FEE2E2' : C.bg, padding:'8px 20px', borderRadius: 10, border:`2px solid ${urgent ? C.error : C.border}` }}>
      {pad(m)}:{pad(s)}
    </div>
  );
}

// Rank available voices, strongly preferring natural British (en-GB) voices.
function rankBritishVoices() {
  const all = (window.speechSynthesis && window.speechSynthesis.getVoices()) || [];
  const en = all.filter(v => v.lang && v.lang.toLowerCase().startsWith('en'));
  const named = ['libby', 'sonia', 'ryan', 'thomas', 'arthur', 'george', 'oliver', 'daniel', 'serena', 'kate', 'stephanie'];
  const score = (v) => {
    const n = (v.name || '').toLowerCase();
    let s = 0;
    if ((v.lang || '').toLowerCase() === 'en-gb') s += 6;            // British
    if (n.includes('natural') || n.includes('neural')) s += 5;       // Edge/Win neural (very human)
    if (n.includes('google uk')) s += 5;                             // Chrome UK (cloud, natural)
    if (n.includes('google')) s += 2;
    if (named.some(x => n.includes(x))) s += 3;                      // known good en-GB voices
    return s;
  };
  return [...en].sort((a, b) => score(b) - score(a));
}

// IELTS Listening audio — reads the passage aloud via the browser's speech
// synthesis, using the most natural British voice available. Plays once (like
// the real exam); alternates a female/male voice for dialogue speakers.
function ListeningAudio({ text }) {
  const [state, setState] = React.useState('idle'); // idle | playing | done | unsupported
  const [ready, setReady] = React.useState(false);
  const [plays, setPlays] = React.useState(0);      // audio may be played up to twice
  const MAX_PLAYS = 2;

  React.useEffect(() => {
    // Voices load asynchronously in most browsers — warm them up.
    const synth = window.speechSynthesis;
    if (!synth) return;
    const load = () => { if ((synth.getVoices() || []).length) setReady(true); };
    load();
    synth.onvoiceschanged = load;
    return () => { try { synth.cancel(); synth.onvoiceschanged = null; } catch (e) {} };
  }, []);

  const stop = () => { try { window.speechSynthesis.cancel(); } catch (e) {} setState('done'); };

  const play = () => {
    if (state === 'playing' || state === 'unsupported' || plays >= MAX_PLAYS) return;
    const synth = window.speechSynthesis;
    if (!synth || typeof SpeechSynthesisUtterance === 'undefined') { setState('unsupported'); return; }
    synth.cancel();
    const voices = rankBritishVoices();
    const v1 = voices[0] || null;                                    // primary (usually female)
    const v2 = voices.find(v => v !== v1 && /(male|man|ryan|thomas|arthur|george|oliver|daniel)/i.test(v.name)) || voices[1] || v1;
    const lines = String(text || '').split('\n').map(l => l.trim()).filter(Boolean);
    const speakerVoice = {};
    const utts = [];
    lines.forEach(line => {
      const m = line.match(/^([A-Za-z][A-Za-z .'-]{0,20}):\s*(.*)$/);
      const speaker = m ? m[1] : null;
      const content = m && m[2] ? m[2] : line;
      const u = new SpeechSynthesisUtterance(content);
      u.rate = 0.92; u.pitch = 1.0; u.lang = 'en-GB';
      if (speaker) {
        if (!speakerVoice[speaker]) speakerVoice[speaker] = (Object.keys(speakerVoice).length % 2 === 0) ? v1 : v2;
        if (speakerVoice[speaker]) u.voice = speakerVoice[speaker];
      } else if (v1) { u.voice = v1; }
      utts.push(u);
    });
    if (!utts.length) { setState('done'); return; }
    utts[utts.length - 1].onend = () => setState('done');
    setPlays(p => p + 1);
    setState('playing');
    utts.forEach(u => synth.speak(u));
  };

  return (
    <div style={{ display:'flex', alignItems:'center', gap: 12, background:'#EBF9FD', border:`1px solid ${C.teal}`, borderRadius: 10, padding:'12px 16px', marginBottom: 14, flexWrap:'wrap' }}>
      {state === 'playing' ? (
        <button onClick={stop} style={{ display:'flex', alignItems:'center', gap: 8, padding:'9px 18px', borderRadius: 8, border:'none', background: C.error, color:'#fff', fontWeight: 700, fontSize: 14, cursor:'pointer' }}>
          ⏹ Stop
        </button>
      ) : (
        <button onClick={play} disabled={state === 'unsupported' || plays >= MAX_PLAYS} style={{ display:'flex', alignItems:'center', gap: 8, padding:'9px 18px', borderRadius: 8, border:'none',
          background: (state !== 'unsupported' && plays < MAX_PLAYS) ? C.teal : '#9CA3AF', color:'#fff', fontWeight: 700, fontSize: 14, cursor: (state !== 'unsupported' && plays < MAX_PLAYS) ? 'pointer' : 'default' }}>
          {state === 'unsupported' ? '⚠ Audio not supported' : plays >= MAX_PLAYS ? '✓ Audio played (no replays left)' : plays === 1 ? '🔁 Replay (1 left)' : '▶ Play audio'}
        </button>
      )}
      <span style={{ fontSize: 12, color: C.grey }}>🎧 British voice · you may play it up to twice.</span>
    </div>
  );
}

function QuestionCard({ q, answer, onAnswer, index, total, hideScript, recordingStart = true }) {
  const opts = [
    { key:'a', label: q.option_a }, { key:'b', label: q.option_b },
    { key:'c', label: q.option_c }, { key:'d', label: q.option_d },
  ].filter(o => o.label);

  const isSpeaking = q.question_type === 'speaking' || q.section === 'speaking';
  const isMcq = !isSpeaking && (q.question_type === 'mcq' || q.question_type === 'tfng');
  const isFill = !isSpeaking && (q.question_type === 'fill_blank' || q.question_type === 'matching');
  const isEssay = !isSpeaking && (q.question_type === 'essay' || q.question_type === 'short_answer');

  return (
    <div style={{ background:'#fff', borderRadius: 16, border:`1px solid ${C.border}`, padding: 28, marginBottom: 20 }}>
      {/* Question number */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.teal, textTransform:'uppercase', letterSpacing: 1 }}>
          Question {index + 1} of {total} · {SECTION_LABELS[q.section] || q.section}
        </span>
        {answer && <span style={{ fontSize: 11, color: C.success, fontWeight: 600 }}>✓ Answered</span>}
      </div>

      {/* Listening audio — shown once per recording (covers its group of questions) */}
      {q.section === 'listening' && q.passage_text && recordingStart && (
        <ListeningAudio text={q.passage_text} />
      )}

      {/* Passage / recording script — listening: once per recording (and only if not hidden) */}
      {q.passage_text && (q.section === 'listening' ? (recordingStart && !hideScript) : true) && (
        <div style={{ background: C.bg, borderRadius: 10, padding: 16, marginBottom: 16, fontSize: 13, lineHeight: 1.7, color: '#374151', borderLeft:`3px solid ${C.teal}`, maxHeight: 200, overflowY:'auto' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.grey, marginBottom: 6, textTransform:'uppercase' }}>{q.section === 'listening' ? 'Recording' : 'Passage / Context'}</div>
          <pre style={{ whiteSpace:'pre-wrap', fontFamily:'inherit', margin: 0 }}>{q.passage_text}</pre>
        </div>
      )}

      {/* Question text */}
      <div style={{ fontSize: 15, color: C.navy, lineHeight: 1.6, marginBottom: 20, fontWeight: 500 }}>
        {q.question_text}
      </div>

      {/* Chart / diagram (e.g. IELTS Writing Task 1) */}
      {q.image_url && (
        <div style={{ textAlign:'center', marginBottom: 20 }}>
          <img src={q.image_url} alt="Task visual" style={{ maxWidth:'100%', border:`1px solid ${C.border}`, borderRadius: 10 }} />
        </div>
      )}

      {/* MCQ options */}
      {isMcq && opts.length > 0 && (
        <div style={{ fontSize: 13, color: C.grey, marginBottom: 10, fontStyle:'italic' }}>
          {q.question_type === 'tfng'
            ? '✏️ Choose TRUE, FALSE or NOT GIVEN based on the passage.'
            : '✏️ Choose the one correct answer.'}
        </div>
      )}
      {isMcq && opts.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
          {opts.map(o => {
            const selected = answer === o.key;
            return (
              <button key={o.key} onClick={() => onAnswer(q.id, o.key)} style={{
                display:'flex', alignItems:'center', gap: 14, padding:'12px 16px', borderRadius: 10,
                border:`2px solid ${selected ? C.teal : C.border}`,
                background: selected ? '#EBF9FD' : '#fff', cursor:'pointer', textAlign:'left',
                transition:'all 0.15s',
              }}>
                <span style={{ width: 28, height: 28, borderRadius: '50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink: 0, fontWeight: 700, fontSize: 13,
                  background: selected ? C.teal : C.bg, color: selected ? '#fff' : C.grey }}>
                  {o.key.toUpperCase()}
                </span>
                <span style={{ fontSize: 14, color: C.navy, fontWeight: selected ? 600 : 400 }}>{o.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Fill blank / matching */}
      {(isFill || (isMcq && opts.length === 0)) && (
        <>
          <div style={{ fontSize: 13, color: C.grey, marginBottom: 8, fontStyle:'italic' }}>
            {q.question_type === 'matching'
              ? '✏️ Type the matching option (e.g. the letter or word) in the box below.'
              : (q.section === 'listening'
                  ? '✏️ Complete the gap (shown as “______”) with ONE word and/or a number from the recording.'
                  : '✏️ Complete the gap (shown as “______”) with ONE word and/or a number from the text.')}
          </div>
          <input type="text" value={answer || ''} onChange={e => onAnswer(q.id, e.target.value)}
            spellCheck={false} autoCorrect="off" autoCapitalize="off" autoComplete="off"
            onPaste={e => e.preventDefault()} onCopy={e => e.preventDefault()} onDrop={e => e.preventDefault()}
            placeholder="Type your answer here..." style={{ width:'100%', padding:'12px 16px', borderRadius: 10, border:`2px solid ${answer ? C.teal : C.border}`, fontSize: 14, boxSizing:'border-box', outline:'none' }} />
        </>
      )}

      {/* Speaking — conducted live with an examiner, no in-app answer */}
      {isSpeaking && (
        <div style={{ background:'#FEF3C7', border:`1px solid #FCD34D`, borderRadius: 10, padding:'12px 16px', fontSize: 13, color:'#92400E', display:'flex', gap: 10, alignItems:'flex-start' }}>
          <span style={{ fontSize: 18 }}>🎤</span>
          <span>This part is conducted <strong>live with your examiner</strong> — there's nothing to type. Read the prompt, prepare your spoken answer, and your examiner will assess and score your Speaking band.</span>
        </div>
      )}

      {/* Essay / writing */}
      {isEssay && (
        <div>
          <textarea value={answer || ''} onChange={e => onAnswer(q.id, e.target.value)}
            spellCheck={false} autoCorrect="off" autoCapitalize="off" autoComplete="off"
            onPaste={e => e.preventDefault()} onCopy={e => e.preventDefault()} onCut={e => e.preventDefault()} onDrop={e => e.preventDefault()}
            rows={8} placeholder="Write your response here..." style={{ width:'100%', padding:'12px 16px', borderRadius: 10, border:`2px solid ${answer ? C.teal : C.border}`, fontSize: 14, boxSizing:'border-box', resize:'vertical', outline:'none', lineHeight: 1.6 }} />
          <div style={{ textAlign:'right', fontSize: 12, color: C.grey, marginTop: 4 }}>
            {(answer || '').split(/\s+/).filter(Boolean).length} words
          </div>
        </div>
      )}
    </div>
  );
}

export default function MockTestPage({ guestToken }) {
  const params = useParams();
  const isGuest = !!guestToken;
  const sessionId = guestToken || params.sessionId;
  const base = isGuest
    ? `/api/guest/mock-tests/session/${guestToken}`
    : `/api/student/mock-tests/session/${params.sessionId}`;
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading'); // loading | ready | in_progress | submitting | done
  const [sessionData, setSessionData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentSection, setCurrentSection] = useState(null);
  const [reachedIdx, setReachedIdx] = useState(0);
  const reachedKey = `mockreached_${sessionId}`;
  const [sections, setSections] = useState([]);
  const [results, setResults] = useState(null);
  const saveTimer = useRef(null);

  // Anti-cheat: detect tab switches
  useEffect(() => {
    const handleVisChange = () => {
      if (document.hidden && phase === 'in_progress') {
        api.post(`${base}/tab-switch`).catch(() => {});
        toast.error('⚠️ Tab switch detected and logged!', { duration: 4000 });
      }
    };
    document.addEventListener('visibilitychange', handleVisChange);
    return () => document.removeEventListener('visibilitychange', handleVisChange);
  }, [phase, sessionId]);

  // Load / start the session — invigilator-gated (waiting / paused / cancelled).
  const pollRef = useRef(null);
  const contentRef = useRef(null);
  const sectionTimeRef = useRef({});    // {sectionKey: accumulated seconds}
  const sectionStartRef = useRef(null); // ms timestamp when current section was entered
  const startOrWait = useCallback(async () => {
    try {
      const r = await api.post(`${base}/start`);
      const data = r.data;
      setSessionData(data);
      const qs = data.questions || [];
      setQuestions(qs);
      if (data.answers && Object.keys(data.answers).length) setAnswers(data.answers);
      const sectionMap = {};
      qs.forEach(q => { if (!sectionMap[q.section]) sectionMap[q.section] = []; sectionMap[q.section].push(q); });
      const secs = Object.keys(sectionMap).map(s => ({ key: s, label: SECTION_LABELS[s] || s, questions: sectionMap[s] }));
      setSections(secs);
      // Restore the section the candidate had reached (survives refresh). The active
      // section is the furthest reached OR the first still-incomplete one — never earlier.
      const ans = (data.answers && Object.keys(data.answers).length) ? data.answers : {};
      const hasAns = (q) => ans[q.id] !== undefined && ans[q.id] !== null && String(ans[q.id]).trim() !== '';
      let lastAnswered = -1;
      secs.forEach((s, i) => { if (s.key !== 'speaking' && s.questions.some(hasAns)) lastAnswered = i; });
      let stored = 0; try { stored = parseInt(localStorage.getItem(reachedKey) || '0', 10) || 0; } catch (e) {}
      const startIdx = Math.min(secs.length - 1, Math.max(stored, lastAnswered, 0));
      setReachedIdx(startIdx);
      setCurrentSection(secs[startIdx] ? secs[startIdx].key : (secs[0] && secs[0].key) || null);
      try { localStorage.setItem(reachedKey, String(startIdx)); } catch (e) {}
      sectionTimeRef.current = data.section_time || {};   // resume accumulated time across reloads
      sectionStartRef.current = Date.now();
      setPhase('in_progress');
    } catch (e) {
      const msg = e.response?.data?.error || '';
      if (msg === 'waiting_for_invigilator') { setPhase('waiting'); pollRef.current = setTimeout(startOrWait, 5000); }
      else if (msg === 'paused') { setPhase('paused'); pollRef.current = setTimeout(startOrWait, 5000); }
      else if (msg.includes('cancelled')) { setPhase('cancelled'); }
      else if (msg.includes('already') || msg.includes('submitted') || msg.includes('marked') || msg.includes('expired')) {
        try { const rr = await api.get(`${base}/results`); setResults(rr.data); setPhase('done'); }
        catch { toast.error('Could not load test'); if (!isGuest) navigate('/my-mock-tests'); }
      } else { toast.error(msg || 'Could not start test'); if (!isGuest) navigate('/my-mock-tests'); }
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    startOrWait();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [startOrWait]);

  // While in progress, detect invigilator pause / cancel / auto-expiry.
  useEffect(() => {
    if (phase !== 'in_progress') return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`${base}/status`);
        if (data.status !== 'in_progress') startOrWait();
      } catch {}
    }, 8000);
    return () => clearInterval(id);
  }, [phase, sessionId, startOrWait]);

  // Auto-save answers every 30s
  const saveAnswer = useCallback((qid, val) => {
    setAnswers(prev => {
      const next = { ...prev, [qid]: val };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        api.post(`${base}/answer`, { question_id: qid, answer: val })
          .catch(e => { if (e.response?.status === 409) submitTest(true); });  // time_up → finalise
      }, 1500);
      return next;
    });
  }, [sessionId]);

  const submitTest = async (auto) => {
    const isAuto = auto === true;   // onClick passes an event; only `true` means timeout/auto-submit
    if (!isAuto) {
      // Manual submit must answer every question (Speaking is live, exempt).
      const allQ = sections.flatMap(s => s.questions || []).filter(q => q.question_type !== 'speaking' && q.section !== 'speaking');
      const unanswered = allQ.filter(q => answers[q.id] === undefined || answers[q.id] === null || String(answers[q.id]).trim() === '');
      if (unanswered.length > 0) {
        toast.error(`You must answer all questions before finishing — ${unanswered.length} of ${allQ.length} still unanswered.`);
        return;
      }
      if (!window.confirm('Are you sure you want to submit? You cannot change answers after submission.')) return;
    }
    commitSectionTime();   // capture time spent in the final section
    setPhase('submitting');
    try {
      const r = await api.post(`${base}/submit`, { answers, auto: isAuto, section_time: sectionTimeRef.current });
      toast.success(isAuto ? "Time's up — your test was submitted automatically." : 'Test submitted successfully!');
      setResults(r.data.session);
      setPhase('done');
    } catch (e) {
      // If the clock already ran out server-side, load whatever was scored.
      const msg = e.response?.data?.error || '';
      if (e.response?.status === 400 && msg.toLowerCase().includes('already')) {
        try { const rr = await api.get(`${base}/results`); setResults(rr.data); setPhase('done'); return; } catch {}
      }
      toast.error(msg || 'Submission failed');
      setPhase('in_progress');
    }
  };

  const answeredCount = Object.keys(answers).length;
  const currentSectionData = sections.find(s => s.key === currentSection);
  const currentQs = currentSectionData?.questions || [];

  // Sequential gating: a section opens only once every earlier answerable
  // section is fully answered. Speaking (live) is always open and never blocks.
  const isSectionComplete = (sec) => sec.key === 'speaking' ||
    sec.questions.every(q => answers[q.id] !== undefined && answers[q.id] !== null && String(answers[q.id]).trim() !== '');
  // One-way progression: only the section you've reached is open. Earlier ones are
  // locked (you've moved on, like a real exam); later ones open as you finish each.
  const isSectionLocked = (key) => sections.findIndex(s => s.key === key) !== reachedIdx;
  const advanceTo = (idx) => {
    if (idx < 0 || idx >= sections.length || idx <= reachedIdx) return;
    commitSectionTime();
    api.post(`${base}/section-time`, { section_time: sectionTimeRef.current }).catch(() => {});
    setReachedIdx(idx);
    try { localStorage.setItem(reachedKey, String(idx)); } catch (e) {}
    setCurrentSection(sections[idx].key);
  };
  const goToSection = (key) => {
    const idx = sections.findIndex(s => s.key === key);
    if (idx === reachedIdx) return;                       // already the active section
    if (idx < reachedIdx) {                                // a section you've already left
      toast.error('That section is locked — you have moved on, just like the real exam.');
      return;
    }
    if (idx === reachedIdx + 1 && currentComplete) { advanceTo(idx); return; }   // next, if current done
    toast.error(`Finish the ${currentSectionData?.label || 'current'} section first — answer all ${currentQs.length} questions.`);
  };
  // Add the time spent in the current section to the running total.
  const commitSectionTime = () => {
    if (sectionStartRef.current && currentSection) {
      const elapsed = (Date.now() - sectionStartRef.current) / 1000;
      sectionTimeRef.current[currentSection] = (sectionTimeRef.current[currentSection] || 0) + elapsed;
    }
    sectionStartRef.current = Date.now();
  };
  const currentComplete = currentSectionData ? isSectionComplete(currentSectionData) : false;

  // Per-section time limits. IELTS uses real test-centre times; other tests split
  // their total evenly across sections. Speaking is live (no timer).
  const sectionMinutes = (() => {
    const slug = (sessionData?.test_type?.slug || '').toLowerCase();
    const totalMin = Math.round((sessionData?.time_limit_seconds || 0) / 60);
    const IELTS = { listening: 30, reading: 60, writing: 60, speaking: 0 };
    const nonSpeaking = sections.filter(s => s.key !== 'speaking');
    const split = nonSpeaking.length ? Math.max(5, Math.round(totalMin / nonSpeaking.length)) : totalMin;
    const map = {};
    sections.forEach(s => {
      if (s.key === 'speaking') map[s.key] = 0;
      else if (slug === 'ielts' && IELTS[s.key] != null) map[s.key] = IELTS[s.key];
      else map[s.key] = split;
    });
    return map;
  })();
  const onSectionExpire = () => {
    if (reachedIdx < sections.length - 1) {
      toast(`Time's up for ${currentSectionData?.label || 'this section'} — moving on.`, { icon: '⏱' });
      advanceTo(reachedIdx + 1);
    } else {
      submitTest(true);
    }
  };

  // Reset scroll to the top of the section whenever the student switches sections.
  useEffect(() => { if (contentRef.current) contentRef.current.scrollTo({ top: 0 }); }, [currentSection]);

  // Persist accumulated section time every 10s so a refresh resumes the right
  // per-section remaining time.
  useEffect(() => {
    if (phase !== 'in_progress') return;
    const id = setInterval(() => {
      commitSectionTime();
      api.post(`${base}/section-time`, { section_time: sectionTimeRef.current }).catch(() => {});
    }, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentSection, base]);
  const currentAnswered = currentQs.filter(q => answers[q.id]).length;

  // LOADING
  if (phase === 'loading') return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background: C.bg, flexDirection:'column', gap: 16 }}>
      <div style={{ width: 48, height: 48, border:`4px solid ${C.border}`, borderTopColor: C.teal, borderRadius:'50%', animation:'spin 1s linear infinite' }} />
      <div style={{ color: C.grey, fontSize: 15 }}>Starting your test...</div>
    </div>
  );

  if (phase === 'waiting' || phase === 'paused') {
    const paused = phase === 'paused';
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background: C.bg, flexDirection:'column', gap: 18, padding: 24, textAlign:'center' }}>
        <div style={{ fontSize: 56 }}>{paused ? '⏸️' : '⏳'}</div>
        <h2 style={{ color: C.navy, margin: 0 }}>{paused ? 'Test Paused' : 'Waiting for the invigilator'}</h2>
        <p style={{ color: C.grey, maxWidth: 460, lineHeight: 1.6, margin: 0 }}>
          {paused
            ? 'Your invigilator has paused the test. It will resume automatically once they continue — please wait and do not close this page.'
            : 'Your test will begin as soon as the invigilator starts it. Please keep this page open and wait.'}
        </p>
        <div style={{ width: 40, height: 40, border:`4px solid ${C.border}`, borderTopColor: C.teal, borderRadius:'50%', animation:'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (phase === 'cancelled') return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background: C.bg, flexDirection:'column', gap: 16, padding: 24, textAlign:'center' }}>
      <div style={{ fontSize: 56 }}>🛑</div>
      <h2 style={{ color: C.navy, margin: 0 }}>Test Cancelled</h2>
      <p style={{ color: C.grey, maxWidth: 460, lineHeight: 1.6, margin: 0 }}>This test was cancelled by the invigilator. Please speak to a member of staff.</p>
      {!isGuest && <button onClick={() => navigate('/my-mock-tests')} style={{ padding:'10px 22px', borderRadius: 10, background: C.teal, color:'#fff', border:'none', cursor:'pointer', fontWeight:700 }}>Back to My Mock Tests</button>}
    </div>
  );

  // RESULTS PAGE
  if (phase === 'done' && results) {
    const { total_score, band_score, cefr_result, section_scores, flagged, test_type, status,
            started_at, submitted_at, time_limit_seconds, section_time } = results;
    const secTime = section_time || {};
    const slug = test_type?.slug || '';
    // IELTS has tutor-marked Writing/Speaking — no final band until the tutor marks.
    const pendingTutor = slug === 'ielts' && status !== 'marked';
    const fmtDur = (secs) => {
      if (secs == null || secs < 0) return '—';
      const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = Math.floor(secs % 60);
      return (h ? `${h}h ` : '') + `${m}m ${s}s`;
    };
    const takenSecs = (started_at && submitted_at) ? (new Date(submitted_at) - new Date(started_at)) / 1000 : null;
    return (
      <div style={{ minHeight:'100vh', background: C.bg, padding: 40 }}>
        <div style={{ maxWidth: 700, margin:'0 auto' }}>
          {/* Score Card */}
          <div style={{ background:'#fff', borderRadius: 20, border:`1px solid ${C.border}`, padding: 40, textAlign:'center', marginBottom: 24, boxShadow:'0 4px 24px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 64 }}>{pendingTutor ? '⏳' : '🎉'}</div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: C.navy, margin:'12px 0 4px' }}>{pendingTutor ? 'Test Submitted' : 'Test Complete!'}</h1>
            <p style={{ color: C.grey, fontSize: 15, marginBottom: 32 }}>{test_type?.name || 'Mock Test'}</p>
            {pendingTutor ? (
              <div style={{ background:'#FEF3C7', border:'1px solid #FCD34D', borderRadius: 12, padding:'18px 24px', margin:'0 auto 8px', maxWidth: 500 }}>
                <div style={{ fontSize: 19, fontWeight: 800, color:'#92400E' }}>⏳ Awaiting tutor marking</div>
                <div style={{ fontSize: 13, color:'#92400E', marginTop: 8, lineHeight: 1.6 }}>Your Writing and Speaking are being marked by your instructor. Your <strong>final IELTS band</strong> will appear here once marking is complete.</div>
              </div>
            ) : (
              <div style={{ display:'flex', justifyContent:'center', gap: 40, marginBottom: 32 }}>
                <div>
                  <div style={{ fontSize: 48, fontWeight: 900, color: C.teal }}>{slug === 'ielts' ? band_score : slug === 'sat' ? band_score : `${Math.round(total_score || 0)}%`}</div>
                  <div style={{ fontSize: 13, color: C.grey }}>{slug === 'ielts' ? 'IELTS Band' : slug === 'sat' ? 'SAT Score' : 'Score'}</div>
                </div>
                {cefr_result && slug === 'cefr' && (
                  <div>
                    <div style={{ fontSize: 48, fontWeight: 900, color: C.purple || '#7C3AED' }}>{cefr_result}</div>
                    <div style={{ fontSize: 13, color: C.grey }}>CEFR Level</div>
                  </div>
                )}
              </div>
            )}
            {flagged && (
              <div style={{ background:'#FEF2F2', border:`1px solid #FECACA`, borderRadius: 10, padding:'12px 20px', marginBottom: 20, color: C.error, fontSize: 13 }}>
                ⚠️ This test was flagged for tab switching. Results may be reviewed by the administrator.
              </div>
            )}
            {(time_limit_seconds || takenSecs != null) && (
              <div style={{ display:'flex', justifyContent:'center', gap: 32, marginTop: 8, fontSize: 13, color: C.grey }}>
                <div>⏱ Time allowed: <strong style={{ color: C.navy }}>{fmtDur(time_limit_seconds)}</strong></div>
                {takenSecs != null && <div>⏳ Time taken: <strong style={{ color: C.navy }}>{fmtDur(takenSecs)}</strong></div>}
              </div>
            )}
          </div>

          {/* Section Scores */}
          {section_scores && Object.keys(section_scores).length > 0 && (
            <div style={{ background:'#fff', borderRadius: 16, border:`1px solid ${C.border}`, padding: 24, marginBottom: 24 }}>
              <h3 style={{ margin:'0 0 16px', color: C.navy, fontSize: 16 }}>Section Breakdown</h3>
              {Object.entries(section_scores).map(([sec, data]) => {
                const isBand = data && data.band != null;                       // tutor-marked IELTS → band
                const pct = isBand ? (Number(data.band) / 9) * 100 : (data && data.percent != null ? data.percent : 0);
                const label = isBand ? `Band ${Number(data.band).toFixed(1)}`
                  : (data && data.correct != null ? `${data.correct}/${data.total} (${data.percent}%)` : `${Math.round(pct)}%`);
                const col = pct >= 70 ? C.success : pct >= 50 ? C.amber : C.error;
                const isSpk = String(sec).toLowerCase() === 'speaking';
                const tSecs = secTime[String(sec).toLowerCase()];
                const timeLabel = isSpk ? '🎤 live with examiner' : (tSecs != null ? `⏳ ${fmtDur(tSecs)}` : null);
                return (
                  <div key={sec} style={{ marginBottom: 16 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>
                        {SECTION_LABELS[sec] || sec}
                        {timeLabel && <span style={{ fontWeight: 400, color: C.grey, marginLeft: 8, fontSize: 12 }}>· {timeLabel}</span>}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: col }}>{label}</span>
                    </div>
                    <div style={{ background: C.bg, borderRadius: 6, height: 8 }}>
                      <div style={{ background: col, height: 8, borderRadius: 6, width:`${Math.max(0, Math.min(100, pct))}%`, transition:'width 0.5s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Examiner feedback (from the live speaking / written marking) */}
          {results.examiner_notes && results.examiner_notes.trim() && (
            <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius: 12, padding: 18, marginBottom: 24 }}>
              <div style={{ fontWeight: 700, color: C.navy, fontSize: 15, marginBottom: 8 }}>🗣️ Examiner feedback</div>
              <div style={{ fontSize: 14, color: '#1E3A5F', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{results.examiner_notes}</div>
            </div>
          )}

          {/* Writing/Speaking pending */}
          {pendingTutor && (
            <div style={{ background:'#FEF3C7', border:`1px solid #FCD34D`, borderRadius: 12, padding: 16, marginBottom: 24, fontSize: 13, color:'#92400E' }}>
              📝 <strong>Writing and Speaking</strong> sections require manual marking by your instructor. Your final band score will be updated once all sections are marked.
            </div>
          )}

          {/* Certificate */}
          {!isGuest && results.certificate && (
            <div style={{ background:'#D1FAE5', border:`1px solid #6EE7B7`, borderRadius: 12, padding: 20, marginBottom: 24, textAlign:'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🏆</div>
              <div style={{ fontWeight: 700, color:'#065F46', marginBottom: 4 }}>Certificate Issued!</div>
              <div style={{ fontSize: 12, color:'#065F46' }}>Verification code: <strong>{results.certificate.verification_code}</strong></div>
            </div>
          )}

          {!isGuest && <button onClick={() => navigate('/dashboard')} style={{ width:'100%', padding:'14px', borderRadius: 12, background: C.teal, color:'#fff', border:'none', cursor:'pointer', fontSize: 15, fontWeight: 700 }}>
            Back to Dashboard
          </button>}
        </div>
      </div>
    );
  }

  // TEST IN PROGRESS
  return (
    <div style={{ minHeight:'100vh', background: C.bg, display:'flex', flexDirection:'column' }}>
      {/* Top bar */}
      <div style={{ background:'#fff', borderBottom:`1px solid ${C.border}`, padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top: 0, zIndex: 100, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ display:'flex', alignItems:'center', gap: 16 }}>
          <img src="/logo192.png" alt="SpeakIQ" style={{ height: 32 }} onError={e => { e.target.style.display='none'; }} />
          <div>
            <div style={{ fontWeight: 700, color: C.navy, fontSize: 14 }}>{sessionData?.test_type?.name || 'Mock Test'}</div>
            <div style={{ fontSize: 11, color: C.grey }}>{answeredCount} of {questions.length} answered</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap: 16 }}>
          {currentSectionData && ((sectionMinutes[currentSection] || 0) > 0 ? (
            <div style={{ textAlign:'right' }}>
              <SectionTimer key={`st-${currentSection}`} sectionKey={currentSection} durationSec={(sectionMinutes[currentSection] || 0) * 60}
                committedRef={sectionTimeRef} startRef={sectionStartRef} onExpire={onSectionExpire} />
              <div style={{ fontSize: 10, color: C.grey, marginTop: 2, letterSpacing: 0.5 }}>{currentSectionData.label.toUpperCase()} · TIME LEFT</div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: C.grey, fontWeight: 600, padding:'8px 16px', background: C.bg, borderRadius: 10 }}>🎤 Live section — no timer</div>
          ))}
          <button onClick={submitTest} disabled={phase === 'submitting'} style={{ padding:'10px 24px', borderRadius: 10, background: C.success, color:'#fff', border:'none', cursor:'pointer', fontSize: 14, fontWeight: 700, opacity: phase === 'submitting' ? 0.7 : 1 }}>
            {phase === 'submitting' ? 'Submitting...' : 'Submit Test'}
          </button>
        </div>
      </div>

      <div style={{ display:'flex', flex: 1 }}>
        {/* Left sidebar — section nav */}
        <div style={{ width: 220, background:'#fff', borderRight:`1px solid ${C.border}`, padding: 16, flexShrink: 0, overflowY:'auto' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.grey, textTransform:'uppercase', letterSpacing: 1, marginBottom: 12 }}>Sections</div>
          {sections.map(sec => {
            const secAnswered = sec.questions.filter(q => answers[q.id]).length;
            const isActive = currentSection === sec.key;
            const locked = isSectionLocked(sec.key);
            return (
              <button key={sec.key} onClick={() => goToSection(sec.key)} style={{
                width:'100%', textAlign:'left', padding:'10px 12px', borderRadius: 8, border:`1px solid ${isActive ? C.teal : 'transparent'}`,
                background: isActive ? '#EBF9FD' : 'transparent', cursor: locked ? 'not-allowed' : 'pointer', marginBottom: 4, transition:'all 0.15s', opacity: locked ? 0.55 : 1,
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: isActive ? C.teal : C.navy }}>{locked ? '🔒 ' : ''}{sec.label}</div>
                {sec.key === 'speaking' ? (
                  <div style={{ fontSize: 11, color: C.grey, marginTop: 2 }}>🎤 {sec.questions.length} prompts · live</div>
                ) : (
                  <>
                    <div style={{ fontSize: 11, color: C.grey, marginTop: 2 }}>{secAnswered}/{sec.questions.length} answered</div>
                    <div style={{ height: 3, borderRadius: 2, background: C.bg, marginTop: 6 }}>
                      <div style={{ height: 3, borderRadius: 2, background: secAnswered === sec.questions.length ? C.success : C.teal, width:`${sec.questions.length > 0 ? (secAnswered/sec.questions.length)*100 : 0}%`, transition:'width 0.3s' }} />
                    </div>
                  </>
                )}
              </button>
            );
          })}

          {/* Quick jump */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.grey, textTransform:'uppercase', letterSpacing: 1, marginBottom: 10 }}>Quick Jump</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap: 4 }}>
              {currentQs.map((q, i) => (
                <a key={q.id} href={`#q-${q.id}`} style={{
                  width: 28, height: 28, borderRadius: 6, display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize: 11, fontWeight: 600, textDecoration:'none',
                  background: answers[q.id] ? C.success : C.bg,
                  color: answers[q.id] ? '#fff' : C.grey,
                  border:`1px solid ${answers[q.id] ? C.success : C.border}`,
                }}>{i+1}</a>
              ))}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div ref={contentRef} style={{ flex: 1, padding:'28px 32px', overflowY:'auto', maxHeight:'calc(100vh - 70px)' }}>
          {currentSectionData && (
            <div style={{ marginBottom: 20, padding:'14px 20px', background:'#fff', borderRadius: 12, border:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontWeight: 700, color: C.navy, fontSize: 16 }}>{currentSectionData.label}</div>
                <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>
                  {currentSectionData.key === 'speaking' ? `${currentQs.length} prompts · conducted live` : `${currentAnswered} of ${currentQs.length} answered`}
                </div>
              </div>
              {currentSectionData.key === 'speaking' ? (
                <div style={{ fontSize: 12, color: C.amber, fontWeight: 600 }}>🎤 Live with examiner</div>
              ) : currentSectionData.key === 'writing' || currentSectionData.key === 'essay' ? (
                <div style={{ fontSize: 12, color: C.amber, fontWeight: 600 }}>✍️ Manually marked by instructor</div>
              ) : (
                <div style={{ fontSize: 12, color: C.success, fontWeight: 600 }}>⚡ Auto-scored</div>
              )}
            </div>
          )}

          {currentQs.map((q, i) => {
            // For listening, the recording/audio belongs to a GROUP of questions and
            // should appear only once — at the first question of each recording.
            const recordingStart = q.section !== 'listening' || i === 0 || (currentQs[i - 1] && currentQs[i - 1].passage_text !== q.passage_text);
            return (
              <div key={q.id} id={`q-${q.id}`}>
                <QuestionCard q={q} answer={answers[q.id]} onAnswer={saveAnswer} index={i} total={currentQs.length} hideScript={sessionData?.hide_listening_script} recordingStart={recordingStart} />
              </div>
            );
          })}

          {/* Section navigation */}
          <div style={{ display:'flex', justifyContent:'space-between', marginTop: 20, paddingBottom: 40 }}>
            <div style={{ fontSize: 12, color: C.grey, alignSelf:'center' }}>🔒 Once you move on, this section locks — like the real exam.</div>
            {sections.indexOf(currentSectionData) < sections.length - 1 ? (
              <button onClick={() => { if (!currentComplete) { toast.error(`Answer all ${currentQs.length} questions in this section before moving on.`); return; } if (window.confirm('Move to the next section? You will NOT be able to return to this section or change its answers afterwards.')) advanceTo(reachedIdx + 1); }}
                disabled={!currentComplete}
                style={{ padding:'10px 20px', borderRadius: 10, border:'none', background: currentComplete ? C.teal : '#9CA3AF', color:'#fff', cursor: currentComplete ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700 }}>
                {currentComplete ? 'Next Section →' : `Answer all ${currentQs.length} to continue`}</button>
            ) : (
              <button onClick={submitTest} style={{ padding:'10px 24px', borderRadius: 10, border:'none', background: C.success, color:'#fff', cursor:'pointer', fontSize: 13, fontWeight: 700 }}>
                ✅ Submit Test
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
